import React from 'react';
import moment from 'moment-timezone';
import I18n from 'modules/i18n';
import Tooltip from '@/components/common/TCIReactTooltip';
import styles from '@/components/staffer/AssignmentsBasedGradesTable/Table/styles.module.scss';
import { IAssignment } from '@/components/interfaces';
import useGradesTableStore from '@/components/staffer/AssignmentsBasedGradesTable/store/useGradesTableStore';
import cn from '@/utils/cn';
import AssignmentOptionsMenu from './AssignmentOptionsMenu';

const AssignmentsRow = () => {
  const assignments = useGradesTableStore(state => state.assignments);

  const assignmentTypeCell = (assignment: IAssignment) => (
    <td
      key={`assignment-type-${assignment.id}`}
      className={styles.assignmentType}
    >
      {assignment.assignment_details.type}
    </td>
  );

  const assignmentDetailsCell = (assignment: IAssignment, lastCell) => {
    const timezone = moment.tz(moment.tz.guess()).zoneAbbr();

    const tooltipContent = (
      <div>
        <div>
          <span className="tw-font-bold">{assignment.assignment_details.type}: </span>
          <span>{assignment.assignment_details.title}</span>
        </div>
        <div>
          <span>{I18n.t('due_date')}: </span>
          <span className="tw-italic">{moment(assignment.due_datetime).format('M/DD/YY')} {timezone}</span>
        </div>
      </div>
    );

    return (
      <th key={assignment.id} className={cn(styles.assignmentDetails, { [styles.lasCell]: lastCell })}>
        <Tooltip
          html={tooltipContent}
          position="top"
          size="small"
          theme="white"
        >
          <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
            <div>
              <div className={cn(styles.assignmentTitle, 'tw-py-0.5')}>{assignment.assignment_details.title}</div>
              <div className="tw-italic tw-font-light tw-text-very-dark-grey tw-py-0.5">
                {I18n.t('due')} {moment(assignment.due_datetime).format('M/DD/YY')}
              </div>
              <div className="tw-py-0.5">{assignment.max_points} {I18n.t('points')}</div>
            </div>

            <AssignmentOptionsMenu assignment={assignment} />
          </div>
        </Tooltip>
      </th>
    );
  };

  const assignmentTypeSpacer = (keyIdentifier: string) => (
    <td
      key={`assignment-type-spacer-${keyIdentifier}`}
      className={cn(styles.assignmentType, styles.spacer, '!tw-p-0 !tw-border-none tw-h-4')}
    />
  );

  const assignmentDetailSpacer = (label?: string, keyIdentifier?: string) => (
    <th
      key={`assignment-detail-spacer-${label || keyIdentifier || 'default'}`}
      className={cn(
        styles.assignmentDetails, styles.spacer,
        '!tw-p-0 tw-h-14',
        { 'tw-text-center tw-align-middle': label },
      )}
    >
      {label && (<p className="tw-m-0 tw-italic tw-text-dark-grey">{I18n.t(label)}</p>)}
    </th>
  );

  const renderAssignmentTypes = () => {
    if (assignments.length < 1) {
      return (
        <>
          {Array.from({ length: 4 }).map((_, i) => (assignmentTypeSpacer(i.toString())))}
        </>
      );
    }

    if (assignments.length < 3) {
      return (
        <>
          {assignments.map(a => assignmentTypeCell(a))}
          {Array.from({ length: 4 - assignments.length }).map((_, i) => (assignmentTypeSpacer(i.toString())))}
        </>
      );
    }

    return assignments.map(a => assignmentTypeCell(a));
  };

  const renderAssignmentDetails = () => {
    if (assignments.length < 1) {
      return (
        <>
          {assignmentDetailSpacer('no_assignments')}
          {Array.from({ length: 3 }).map((_, i) => (assignmentDetailSpacer(null, i.toString())))}
        </>
      );
    }

    if (assignments.length < 3) {
      return (
        <>
          {assignments.map((a, i) => assignmentDetailsCell(a, i === assignments.length - 1))}
          {Array.from({ length: 4 - assignments.length }).map((_, i) => (assignmentDetailSpacer(null, i.toString())))}
        </>
      );
    }

    return assignments.map((a, i) => assignmentDetailsCell(a, i === assignments.length - 1));
  };

  return (
    <>
      <tr>
        {assignmentTypeSpacer('type-spacer')}
        {renderAssignmentTypes()}
      </tr>
      <tr>
        {assignmentDetailSpacer()}
        {renderAssignmentDetails()}
      </tr>
    </>
  );
};

export default AssignmentsRow;
